import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";

import https from "https";
import axios from "axios";
import log from "../utils/log";
import metaData from "../data/metaData";
import c from "../utils/common";

//
import useAuthStore from "../states/useAuth";
import useRequestStore from "../states/useRequest";
import useUserStore from "../states/useUser";
import useDB from "../hooks/useDB";

const useUser = () => {
  const { auth, user, updateUser: updateUserToCache } = useAuthStore();
  const {} = useRequestStore();
  const {
    getSingleUser: getSingleUserFromCache,
    getUsers: getUsersFromCache,
    updateSingleUserCache,
    updateUsersCache,
  } = useUserStore();

  const { get } = useDB();

  /**
   * getSingleUser:
   * This function fetches a single user by its ID, first checking if the user's data is cached.
   *
   * - If the user data is found in the cache, it returns the cached data.
   * - If the user data is not found in the cache, it simulates a delay (e.g., 2000ms),
   *   fetches the data (simulated here by a temporary object), stores it in the cache,
   *   and then returns the fetched data.
   *
   * @param {number|string} id - The unique ID of the user to fetch.
   * @returns {Object} - The user data, either from the cache or fetched via API.
   */
  const getSingleUser = async (id, saveCache = true) => {
    if (!id) {
      return undefined;
    }

    let cache = getSingleUserFromCache(id);

    if (!cache) {
      log.debug("no cache, axios");

      let response = await get("merchantUser/query", { id });

      // Cache the fetched user data
      if (saveCache) {
        updateSingleUserCache("update", [response]);
      }
      return response; // Return the fetched data
    } else {
      log.debug("using cache");
      return cache; // Return the cached data
    }
  };

  /**
   * getUsers:
   * This function fetches a list of users based on a query (such as pagination or filters),
   * first checking if the query result is cached.
   *
   * - If the query result is found in the cache, it returns the cached data.
   * - If the query result is not found in the cache, it simulates a delay (e.g., 2000ms),
   *   fetches the data (simulated here by a temporary array), stores it in the cache,
   *   and then returns the fetched data.
   *
   * @param {string} query - The query string to filter or paginate the users (e.g., "?status=0&pagination=1").
   * @returns {Array} - The list of users, either from the cache or fetched via API.
   */
  const getUsers = async (query, saveCache = true, useCache = true) => {
    let cache = useCache ? getUsersFromCache(query) : null;
    if (cache) {
      log.debug("using cache");
      return cache; // Return cached data if available
    }

    log.debug("no cache, axios");

    // prepare body
    let body;
    if (query === undefined || typeof query === "string") {
      //transform to json
      body = {
        companyId: user?.companyId,
      };
    } else if (typeof query === "object") {
      // If query is an object, assume it's already in the correct format
      body = query;
    } else {
      throw new Error("Invalid query format. It must be either undefined, a string, or an object.");
    }

    let response = await get("merchantUsers/query", body);

    // Cache the fetched requests data
    if (typeof query !== "object" && saveCache) {
      updateUsersCache("update", query, response); // Cache the fetched users data
    }
    return response; // Return the fetched data
  };

  const createUser = async (data) => {
    log.debug(data);

    let res = await axios.post(metaData.serverURL + "merchantUser/new", data, {
      headers: { Authorization: auth.jwt },
      httpsAgent: new https.Agent({ keepAlive: true, keepAliveMsecs: 60000 }),
    });
    // log.debug(res);

    updateSingleUserCache("update", [res.data.user]); // Cache the fetched request data
    return res.data.user;
  };

  const updateUser = async (userId, data, saveCache = true) => {
    const response = await axios.patch(metaData.serverURL + "merchantUser", data, {
      params: { id: userId }, // Pass the request ID as a query parameter if needed
      headers: { Authorization: auth.jwt },
      httpsAgent: new https.Agent({ keepAlive: true, keepAliveMsecs: 60000 }),
    });
    // log.debug(response.data);

    // Cache the fetched request data
    if (saveCache) {
      updateSingleUserCache("update", [response.data]);
    }

    // Update
    if (response.data.id == user.id) {
      updateUserToCache("whole", response.data);
    }

    // Clear
    updateUsersCache("whole", {});
    return response.data; // Return the updated data
  };

  const resetPassword = async (userId, data, saveCache = true) => {
    const response = await axios.patch(metaData.serverURL + "merchantUser/resetPassword", data, {
      params: { id: userId }, // Pass the request ID as a query parameter if needed
      headers: { Authorization: auth.jwt },
      httpsAgent: new https.Agent({ keepAlive: true, keepAliveMsecs: 60000 }),
    });
    // log.debug(response.data);

    // Cache the fetched request data
    if (saveCache) {
      updateSingleUserCache("update", [response.data]);
    }

    // Update
    if (response.data.id == user.id) {
      updateUserToCache("whole", response.data);
    }

    // Clear
    updateUsersCache("whole", {});
    return response.data; // Return the updated data
  };

  return { getSingleUser, getUsers, createUser, updateUser, resetPassword };
};

export default useUser;
